'use client';

import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Ban } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@kit/ui/accordion';
import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import { Input } from '@kit/ui/input';
import { Karte } from '@kit/ui/karte';
import { Label } from '@kit/ui/label';
import { RadioGroup, RadioGroupItem } from '@kit/ui/radio-group';
import { Segment } from '@kit/ui/segment';
import { Trans } from '@kit/ui/trans';

import { type SelfDisclosureConfig } from '../lib/config.schema';
import {
  CITY_MESSAGE,
  CONTEXTUAL_MESSAGES,
  DISPLAY_NAME_MESSAGE,
  type DerivedState,
  EMAIL_MESSAGE,
  type GeneratorDraft,
  INCOME_KEYS,
  type IncomeChoice,
  POSTAL_CODE_MESSAGE,
  STREET_MESSAGE,
  type SelfDisclosureErrorField,
  applyPreset,
  configToDraft,
  currentIncomeChoice,
  defaultDraft,
  deriveState,
  detectPresetKey,
  detectPresetName,
  firstErrorField,
  isThresholdMessage,
  lockReason,
  sanitizeEnabled,
  setIncomeChoice,
  toggleField,
} from '../lib/draft-state';
import {
  FIELD_SECTION_LABELS,
  FIELD_SECTION_ORDER,
  FORBIDDEN_CATEGORIES,
  RECOMMENDED_FROM_LABEL,
  SELF_DISCLOSURE_FIELDS,
  SELF_DISCLOSURE_PRESETS,
  type SelfDisclosureField,
  type SelfDisclosurePreset,
  getFieldByKey,
  presetAmpel,
  presetBeschreibung,
  presetName,
  textFuerSprache,
} from '../lib/fields';

/**
 * Shared, app-agnostic Mieterselbstauskunft generator form v2
 * (Sanierungs-Spec §2.6). Reused by the marketing landing page island and the
 * Settings "Vorlagen" section. Props-driven, no app-specific imports.
 *
 * Steps (E3 pivot): 1. Grundlagen (language, persons, options, presets) →
 * 2. Felder (traffic-light catalog incl. the non-selectable red section
 * "Das darfst du nicht fragen") → 3. Absender.
 *
 * DELIBERATE EXCEPTION to the CLAUDE.md rule "always use react-hook-form":
 * this form is a catalog-toggle UI without per-field error semantics – the
 * validation is config-derivative (the shared Zod schema runs on every
 * change, `deriveState`). react-hook-form would add registration overhead
 * without any benefit. Documented in Sanierungs-Spec §2.6/M8.
 *
 * State lives entirely inside this component (a single draft object plus one
 * UI-state object). On every change it derives a `SelfDisclosureConfig` via
 * the shared Zod schema and hands it up through `onConfigChange` (null while
 * incomplete/invalid). `onDirty` fires exactly once on the FIRST user
 * interaction (never on mount) – wrappers hook analytics to it (M9).
 * Terminal actions (Download / Save / Preview) are NOT part of this
 * component – the wrapper provides them via `renderFooter`.
 */

type Language = SelfDisclosureConfig['language'];

export interface GeneratorFormRenderState extends DerivedState {
  /**
   * Reveals validation errors (Spec §I.4/M11): marks the form as "attempted",
   * jumps to the earliest error step and scrolls/focuses the offending field.
   * Wrappers call this when a terminal action is clicked while `config` is
   * null – instead of a dead disabled button.
   */
  revealErrors: () => void;
}

export interface GeneratorFormProps {
  /** Prefill for the edit case (Settings). */
  initialConfig?: SelfDisclosureConfig | null;
  /** Fired on every change with the derived config (null when invalid). */
  onConfigChange?: (config: SelfDisclosureConfig | null) => void;
  /**
   * Fired exactly ONCE on the first user interaction (never on mount).
   * Replaces the former mountSeen heuristic in wrappers (Spec §2.6/M9).
   */
  onDirty?: () => void;
  /** Terminal actions rendered at the bottom (Download / Save / …). */
  renderFooter?: (state: GeneratorFormRenderState) => React.ReactNode;
  /** Root className override. */
  className?: string;
  /** Prefix for stable `data-test` selectors (default: "generator"). */
  dataTestPrefix?: string;
  /**
   * Klassen für die Schritt-Knöpfe, vom Aufrufer gesetzt.
   *
   * 🔴 WARUM DAS EINE EIGENE EIGENSCHAFT IST. Bis zum 19.08.2026 gestaltete
   * `marketing-extra.css` diese Knöpfe über ihre `data-test`-Attribute
   * (`[data-test='generator-step-back']`). Ein Test-Attribut umzubenennen hätte
   * damit still das Website-Layout zerlegt — und niemand hätte es beim
   * Umbenennen gesehen, weil ein `data-test` nach einem Test-Detail aussieht,
   * nicht nach einem Gestaltungs-Vertrag.
   *
   * Jetzt setzt die Website ihr Aussehen selbst, und die Komponente bleibt
   * app-agnostisch: sie weiß nicht, in welchem Kontext sie steht.
   */
  schrittKnopfKlassen?: { zurueck?: string; weiter?: string };
}

const STEPS = ['basics', 'fields', 'sender'] as const;
type Step = (typeof STEPS)[number];

/**
 * 🔴 DIE WEBSITE-FASSUNG HAT DIE SCHLUESSEL NICHT MITBEKOMMEN.
 *
 * Die App-native Fassung derselben Fläche
 * (`app/home/[account]/settings/templates/_components/app-generator/basics-section.tsx`)
 * läuft seit dem 23.08.2026 vollständig über `self-disclosure:generator.*`.
 * Hier standen dieselben Beschriftungen weiter als deutscher Fixtext — auf der
 * ÖFFENTLICHEN Seite `/mieterselbstauskunft`, also genau dort, wo jemand mit
 * englischer oder französischer Oberfläche ankommt.
 *
 * Die Wörter sind die aus Blatt Q2: „Deutsch · English · Beides". „Deutsch"
 * und „English" bleiben in ihrer eigenen Sprache stehen (so nennt man eine
 * Sprachwahl überall) — sie gehen trotzdem über Schlüssel, weil eine Ausnahme
 * von der Übersetzungspflicht die Stelle ist, an der die nächste entsteht.
 *
 * ⚠️ `draft.language` ist die Sprache des ERZEUGTEN Formulars, nicht die der
 * Bedienung. Beschriftet wird sie nach der Oberflächen-Sprache; was im PDF
 * landet, bleibt davon unberührt.
 */
/*
 * 🔴 FERTIGE BESCHRIFTUNGEN, KEINE SCHLÜSSEL-TABELLE.
 *
 * Der naheliegende Weg wäre `Record<Language, string>` mit den Schlüsseln und
 * dann `<Trans i18nKey={tabelle[wert]} />`. Der Roh-Key-Riegel
 * (`check:i18n-raw-keys`, Stufe L2) weist das ab, und er hat recht: ein
 * dynamischer Schlüssel kann zur Laufzeit ins Leere zeigen, und dann steht der
 * Schlüssel selbst auf dem Bildschirm.
 *
 * Ein `Record` über die VOLLE Vereinigung mit festen Schlüsseln im JSX löst
 * beides: TypeScript erzwingt die Vollständigkeit, und jeder Schlüssel ist eine
 * Zeichenkette im Quelltext — auffindbar, prüfbar, nicht zusammengesetzt.
 * Dieselbe Bauform benutzt die App-native Fassung.
 */
const SPRACHE_LABEL: Record<Language, ReactNode> = {
  de: <Trans i18nKey="self-disclosure:generator.sprache.de" />,
  en: <Trans i18nKey="self-disclosure:generator.sprache.en" />,
  'de-en': <Trans i18nKey="self-disclosure:generator.sprache.beides" />,
};

const LANGUAGE_OPTIONS: { value: Language; label: ReactNode }[] = [
  { value: 'de', label: SPRACHE_LABEL.de },
  { value: 'en', label: SPRACHE_LABEL.en },
  { value: 'de-en', label: SPRACHE_LABEL['de-en'] },
];

/** Die drei Schritte des Assistenten — Nummer und Wort stehen im Katalog. */
const SCHRITT_LABEL: Record<Step, ReactNode> = {
  basics: <Trans i18nKey="self-disclosure:generator.schritt.grundlagen" />,
  fields: <Trans i18nKey="self-disclosure:generator.schritt.felder" />,
  sender: <Trans i18nKey="self-disclosure:generator.schritt.absender" />,
};

// ── Wizard error-target adapter (Spec §2.3) ───────────────────────────────────

const ERROR_FIELD_STEP: Record<SelfDisclosureErrorField, Step> = {
  'income-threshold': 'fields',
  'sender-display-name': 'sender',
  'sender-street': 'sender',
  'sender-postal-code': 'sender',
  'sender-city': 'sender',
  'sender-email': 'sender',
};

/**
 * Thin wizard adapter over the isomorphic `firstErrorField`: maps the semantic
 * error field onto its wizard step and the prefixed `data-test` element id. The
 * dt suffixes are byte-identical to the pre-extraction literals, so scroll/focus
 * targets and step jumps are unchanged.
 */
function firstErrorTarget(
  draft: GeneratorDraft,
  errors: string[],
  dt: (suffix: string) => string,
): { step: Step; elementId: string } | null {
  const field = firstErrorField(draft, errors);

  if (!field) return null;

  return { step: ERROR_FIELD_STEP[field], elementId: dt(field) };
}

// ── Component ─────────────────────────────────────────────────────────────────

interface UiState {
  step: Step;
  /** True once the user has toggled fields manually (preset-confirm guard). */
  fieldsTouched: boolean;
  /** Preset waiting for confirmation because fields were touched manually. */
  pendingPresetKey: string | null;
  /** True after a terminal action was attempted with an invalid config (M9). */
  attempted: boolean;
  /** Monotonic counter – each revealErrors bump re-triggers the scroll effect
   *  even when the error step equals the current step (Spec §I.1, Problem B). */
  revealTick: number;
  /** Blur-tracked touched flags for the validation-relevant fields (§I.2). */
  touched: {
    displayName: boolean;
    street: boolean;
    postalCode: boolean;
    city: boolean;
    email: boolean;
    threshold: boolean;
  };
}

type TouchedField = keyof UiState['touched'];

export function GeneratorForm({
  initialConfig,
  onConfigChange,
  onDirty,
  renderFooter,
  className,
  dataTestPrefix = 'generator',
  schrittKnopfKlassen,
}: GeneratorFormProps) {
  const [draft, setDraftRaw] = useState<GeneratorDraft>(() =>
    initialConfig ? configToDraft(initialConfig) : defaultDraft(),
  );
  const [ui, setUi] = useState<UiState>({
    step: 'basics',
    fieldsTouched: false,
    pendingPresetKey: null,
    attempted: false,
    revealTick: 0,
    touched: {
      displayName: false,
      street: false,
      postalCode: false,
      city: false,
      email: false,
      threshold: false,
    },
  });

  const state = useMemo(() => deriveState(draft), [draft]);

  // Always-current draft snapshot for the stable `revealErrors` callback – lets
  // it read the latest draft without being recreated on every keystroke.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const formRootRef = useRef<HTMLDivElement>(null);

  const dirtyNotified = useRef(false);

  // Single justified effect: surface the derived config to the wrapper
  // whenever it changes (including the initial mount) so the wrapper's own
  // action buttons can react. There is no cleaner way to notify a parent of
  // derived state; notifying during render would break React's rules.
  useEffect(() => {
    onConfigChange?.(state.config);
  }, [state.config, onConfigChange]);

  const markDirty = () => {
    if (!dirtyNotified.current) {
      dirtyNotified.current = true;
      onDirty?.();
    }
  };

  const setDraft = (updater: (prev: GeneratorDraft) => GeneratorDraft) => {
    markDirty();
    setDraftRaw(updater);
  };

  const stepIndex = STEPS.indexOf(ui.step);

  const goTo = (step: Step | undefined) => {
    if (step) setUi((prev) => ({ ...prev, step }));
  };

  const dt = useCallback(
    (suffix: string) => `${dataTestPrefix}-${suffix}`,
    [dataTestPrefix],
  );

  const markTouched = useCallback((field: TouchedField) => {
    setUi((prev) => ({
      ...prev,
      touched: { ...prev.touched, [field]: true },
    }));
  }, []);

  // Terminal-action-invalid entry point (Spec §I.1/§I.4): reveal all errors,
  // jump to the earliest error step and bump revealTick so the scroll effect
  // fires even when the error step equals the current step (Problem B).
  const revealErrors = useCallback(() => {
    const currentDraft = draftRef.current;
    const target = firstErrorTarget(
      currentDraft,
      deriveState(currentDraft).errors,
      dt,
    );

    setUi((prev) => ({
      ...prev,
      attempted: true,
      step: target ? target.step : prev.step,
      revealTick: prev.revealTick + 1,
    }));
  }, [dt]);

  // (a) Step change → scroll to the start of the new step (Spec §I.1 Problem A).
  // useLayoutEffect: run before paint so no flash of the old scroll position.
  const isFirstRender = useRef(true);
  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    formRootRef.current?.scrollIntoView({ block: 'start' });
  }, [ui.step]);

  // (b) reveal impulse → scroll/focus the concrete error field (Problem A + B).
  // Intentionally keyed on revealTick only: it must fire on every reveal, using
  // the latest draft/errors from the fresh closure (recreated each render).
  useLayoutEffect(() => {
    if (ui.revealTick === 0) return;

    const target = firstErrorTarget(draft, state.errors, dt);
    const el = target ? document.getElementById(target.elementId) : null;

    (el ?? formRootRef.current)?.scrollIntoView({ block: 'center' });
    el?.focus?.({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ui.revealTick]);

  const handlePresetClick = (preset: SelfDisclosurePreset) => {
    markDirty();

    if (ui.fieldsTouched && ui.pendingPresetKey !== preset.key) {
      setUi((prev) => ({ ...prev, pendingPresetKey: preset.key }));

      return;
    }

    setDraftRaw((prev) => applyPreset(prev, preset));
    setUi((prev) => ({
      ...prev,
      fieldsTouched: false,
      pendingPresetKey: null,
    }));
  };

  const fallbackErrors = ui.attempted
    ? state.errors.filter(
        (message) =>
          !CONTEXTUAL_MESSAGES.has(message) && !isThresholdMessage(message),
      )
    : [];

  return (
    <div
      ref={formRootRef}
      className={className ?? 'space-y-4'}
      data-test={dt('form')}
      data-step={ui.step}
    >
      <ConfigSummary draft={draft} dt={dt} />

      <StepIndicator step={ui.step} />

      {ui.step === 'basics' && (
        <BasicsStep
          draft={draft}
          setDraft={setDraft}
          pendingPresetKey={ui.pendingPresetKey}
          onPresetClick={handlePresetClick}
          onPresetCancel={() =>
            setUi((prev) => ({ ...prev, pendingPresetKey: null }))
          }
          dt={dt}
        />
      )}

      {ui.step === 'fields' && (
        <FieldsStep
          draft={draft}
          setDraft={(updater) => {
            setUi((prev) => ({ ...prev, fieldsTouched: true }));
            setDraft(updater);
          }}
          dt={dt}
          attempted={ui.attempted}
          thresholdTouched={ui.touched.threshold}
          onMarkTouched={markTouched}
        />
      )}

      {ui.step === 'sender' && (
        <SenderStep
          draft={draft}
          setDraft={setDraft}
          dt={dt}
          attempted={ui.attempted}
          touched={ui.touched}
          onMarkTouched={markTouched}
          errors={state.errors}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="quiet"
            onClick={() => goTo(STEPS[stepIndex - 1])}
            disabled={stepIndex === 0}
            className={schrittKnopfKlassen?.zurueck}
            data-test={dt('step-back')}
          >
            <Trans i18nKey="common:back" />
          </Button>

          {stepIndex < STEPS.length - 1 && (
            <Button
              type="button"
              variant="quiet"
              onClick={() => goTo(STEPS[stepIndex + 1])}
              className={schrittKnopfKlassen?.weiter}
              data-test={dt('step-next')}
            >
              <Trans i18nKey="common:continue" />
            </Button>
          )}
        </div>

        {renderFooter && stepIndex === STEPS.length - 1 ? (
          <div>{renderFooter({ ...state, revealErrors })}</div>
        ) : null}
      </div>

      {/* Gedämpfter Fallback-Block (Spec §I.2/2b): only after a submit attempt,
          only for schema errors NOT covered contextually (fail-open to
          visibility – new schema rules land here automatically). */}
      {fallbackErrors.length > 0 && (
        <ul
          className="text-muted-foreground space-y-1 text-sm"
          data-test={dt('errors-fallback')}
        >
          {fallbackErrors.map((error, index) => (
            <li key={index} className="flex items-start gap-2">
              <Ban className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{error}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Live summary header (Spec §2.6/M7) ────────────────────────────────────────

/**
 * 🔴 Die Zusammenfassungszeile war der dichteste Fixtext-Knoten der Fläche:
 * „Deine Selbstauskunft:", die Sprachbezeichnung aus `LANGUAGE_SUMMARY_LABEL`
 * (Deutsch/Englisch/Zweisprachig), „1 Person"/„… Personen", „+ Bürge" und
 * „… Felder" — fünf deutsche Wörter in einem Satz, auf einer öffentlichen
 * Seite.
 *
 * Die Sprachbezeichnung geht jetzt über DIESELBEN Schlüssel wie die Auswahl
 * darüber (`generator.sprache.*`). Das ist nicht nur weniger Text, sondern
 * behebt eine echte Abweichung: die Auswahl bot „Deutsch · English · Beides"
 * an, die Zusammenfassung nannte dieselbe Wahl „Deutsch · Englisch ·
 * Zweisprachig". Zwei Namen für einen Wert, im selben Bildschirm.
 *
 * ⚠️ `detectPresetName(draft)` bleibt, wie es ist: das ist der Name der
 * VORLAGE und hängt an `draft.language`, also am erzeugten Dokument — nicht an
 * der Bediensprache.
 */
function ConfigSummary({
  draft,
  dt,
}: {
  draft: GeneratorDraft;
  dt: (suffix: string) => string;
}) {
  return (
    <div
      className="border-input text-muted-foreground rounded-lg border px-3 py-2 text-xs"
      data-test={dt('summary')}
    >
      <span className="text-foreground font-medium">
        <Trans i18nKey="self-disclosure:generator.zusammenfassung" />{' '}
      </span>
      {SPRACHE_LABEL[draft.language]} ·{' '}
      <Trans
        i18nKey="self-disclosure:generator.personenZahl"
        values={{ count: draft.personCount }}
      />
      {draft.includeGuarantor ? (
        <>
          {' '}
          <Trans i18nKey="self-disclosure:generator.plusBuerge" />
        </>
      ) : null}{' '}
      ·{' '}
      <Trans
        i18nKey="self-disclosure:generator.feldZahl"
        values={{ count: draft.enabledFields.length }}
      />{' '}
      · {detectPresetName(draft)}
    </div>
  );
}

/**
 * ===========================================================================
 * Der Fortschritt NENNT DIE ZAHL — Blatt Q · Q8, Befund 438 (08.09.2026)
 * ===========================================================================
 *
 * Q8: „Der Fortschritt nennt die Zahl («Schritt 2 von 3»), **nicht nur einen
 * Balken** — sonst weiss niemand, wie lange es noch dauert."
 *
 * Hier standen nur die drei Marken. Sie sagen, WO man ist, aber nicht, WIE
 * VIELE es sind: bei 390 px umbrechen sie, und dann steht die aktive Marke
 * allein in einer Zeile — eine Marke ohne Nachbarn ist kein Fortschritt,
 * sondern eine Ueberschrift.
 *
 * 🔴 UND WARUM DIESE FLAECHE UEBERHAUPT SCHRITTE HAT, obwohl Q3 „eine Seite,
 * kein Assistent" sagt: der Satz gilt der APP. Q8 loest den scheinbaren
 * Widerspruch an der Aufgabe — „in der App **pflegt** man Vorlagen und springt
 * zwischen Abschnitten, auf der Website laeuft man **einmal** durch, ohne
 * Konto, oft am Telefon. Die Form folgt der Aufgabe, nicht dem Geschmack."
 * Diese Komponente hat nur noch EINEN Aufrufer, und das ist die Website
 * (`app/(marketing)/mieterselbstauskunft/`); die App hat seit Q2 ihren eigenen,
 * seitenfoermigen Generator.
 *
 * ⚠️ Die Zahlen kommen aus `STEPS`, nicht aus dem Text. Eine getippte „3" im
 * Sprachkatalog waere beim vierten Abschnitt still falsch — dieselbe Regel wie
 * beim Schrittzaehler des Onboardings („NIE eine Step-Zahl hartkodieren").
 */
function StepIndicator({ step }: { step: Step }) {
  const nummer = STEPS.indexOf(step) + 1;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap gap-2">
        {STEPS.map((s) => (
          <span
            key={s}
            className={
              s === step
                ? 'bg-primary text-primary-foreground rounded-full px-3 py-1 text-xs font-medium'
                : 'bg-muted text-muted-foreground rounded-full px-3 py-1 text-xs font-medium'
            }
          >
            {SCHRITT_LABEL[s]}
          </span>
        ))}
      </div>

      <span
        className="text-muted-foreground text-xs font-medium"
        data-test="generator-schritt-zaehler"
      >
        <Trans
          i18nKey="self-disclosure:generator.schritt.zaehler"
          values={{ nummer, gesamt: STEPS.length }}
        />
      </span>
    </div>
  );
}

function OptionToggle({
  label,
  checked,
  onChange,
  dataTest,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  dataTest: string;
}) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <Checkbox
        className="mt-0.5"
        checked={checked}
        onCheckedChange={(value) => onChange(value === true)}
        data-test={dataTest}
      />
      <span>{label}</span>
    </label>
  );
}

// ── Step 1: Grundlagen ────────────────────────────────────────────────────────

function BasicsStep({
  draft,
  setDraft,
  pendingPresetKey,
  onPresetClick,
  onPresetCancel,
  dt,
}: {
  draft: GeneratorDraft;
  setDraft: (updater: (prev: GeneratorDraft) => GeneratorDraft) => void;
  pendingPresetKey: string | null;
  onPresetClick: (preset: SelfDisclosurePreset) => void;
  onPresetCancel: () => void;
  dt: (suffix: string) => string;
}) {
  // Welches Preset entspricht exakt der aktuellen Feld-Auswahl? Dann leuchtet
  // die Karte als "aktiv" – sichtbares Klick-Feedback; erlischt, sobald der
  // Nutzer ein Feld manuell ändert (Auswahl matcht dann kein Preset mehr).
  const activePresetKey = detectPresetKey(draft);
  const pendingPreset =
    SELF_DISCLOSURE_PRESETS.find((preset) => preset.key === pendingPresetKey) ??
    null;

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <p className="text-sm font-medium">
          <Trans i18nKey="self-disclosure:generator.formLanguage" />
        </p>
        <RadioGroup
          value={draft.language}
          onValueChange={(value) =>
            setDraft((prev) => ({ ...prev, language: value as Language }))
          }
          className="flex flex-wrap gap-4"
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 text-sm"
            >
              <RadioGroupItem
                value={option.value}
                data-test={dt(`language-${option.value}`)}
              />
              {option.label}
            </label>
          ))}
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <p id={dt('persons-label')} className="text-sm font-medium">
          <Trans i18nKey="self-disclosure:generator.personCount" />
        </p>
        {/*
          🔴 `flex-wrap`: bei 390 px passen „1 Person · 2 Personen · 3 Personen"
          NICHT nebeneinander. Knoepfe brechen ihren Text nicht um
          (`whitespace-nowrap` an der Schaltflaeche), also schiebt die Zeile den
          Behaelter ueber den Rand statt zu schrumpfen.

          Gemessen am 24.09.2026 von S9b (`specs/smoke/mobil-flaechen.spec.ts`)
          auf `/mieterselbstauskunft`:
            „Container ueber den Rand um 36 px — div.hero-inner …
             ⟵ [data-test="generator-persons-3"]"

          Additiv: oberhalb von 390 px aendert sich nichts, weil dort kein
          Umbruch noetig ist.
        */}
        <Segment
          beschriftetVon={dt('persons-label')}
          optionen={(['1', '2', '3'] as const).map((anzahl) => ({
            key: anzahl,
            label: (
              <Trans
                i18nKey="self-disclosure:generator.personenZahl"
                values={{ count: Number(anzahl) }}
              />
            ),
            dataTest: dt(`persons-${anzahl}`),
          }))}
          wert={String(draft.personCount) as '1' | '2' | '3'}
          onWechsel={(anzahl) =>
            setDraft((prev) => ({ ...prev, personCount: Number(anzahl) }))
          }
        />
        <p className="text-muted-foreground text-sm">
          <Trans i18nKey="self-disclosure:generator.multiPersonHint" />
        </p>
      </div>

      <div className="space-y-2">
        <OptionToggle
          label={<Trans i18nKey="self-disclosure:generator.buergenSpalte" />}
          checked={draft.includeGuarantor}
          onChange={(checked) =>
            setDraft((prev) => ({ ...prev, includeGuarantor: checked }))
          }
          dataTest={dt('include-guarantor')}
        />
        <p className="text-muted-foreground text-sm">
          <Trans i18nKey="self-disclosure:generator.guarantorHint" />
        </p>
      </div>

      <div className="border-input rounded-card space-y-2 border p-3">
        <p className="text-sm font-medium">
          <Trans i18nKey="self-disclosure:generator.options" />
        </p>
        <OptionToggle
          label={<Trans i18nKey="self-disclosure:generator.wbsWohnung" />}
          checked={draft.fieldOptions.isSocialHousing}
          onChange={(checked) =>
            setDraft((prev) =>
              sanitizeEnabled({
                ...prev,
                fieldOptions: {
                  ...prev.fieldOptions,
                  isSocialHousing: checked,
                },
              }),
            )
          }
          dataTest={dt('option-social-housing')}
        />
        <OptionToggle
          label={
            <Trans i18nKey="self-disclosure:generator.oeffentlicherZahler" />
          }
          checked={draft.fieldOptions.publicPayerDirect}
          onChange={(checked) =>
            setDraft((prev) =>
              sanitizeEnabled({
                ...prev,
                fieldOptions: {
                  ...prev.fieldOptions,
                  publicPayerDirect: checked,
                },
              }),
            )
          }
          dataTest={dt('option-public-payer')}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">
          <Trans i18nKey="self-disclosure:generator.quickstart" />
        </p>
        <p className="text-muted-foreground text-sm">
          <Trans i18nKey="self-disclosure:generator.presetQuickstart" />
        </p>
        <div className="grid gap-2">
          {SELF_DISCLOSURE_PRESETS.map((preset) => (
            <button
              key={preset.key}
              type="button"
              onClick={() => onPresetClick(preset)}
              data-test={dt(`preset-${preset.key}`)}
              aria-pressed={preset.key === activePresetKey}
              className={`w-full cursor-pointer rounded-lg border p-3 text-left transition-colors ${
                preset.key === activePresetKey
                  ? 'border-primary bg-accent ring-primary ring-1'
                  : 'border-input hover:bg-accent/50'
              }`}
            >
              {/*
                Q2: „Drei Sätze mit Ampel-Punkt und Feldzahl «Sparsam · Üblich
                · Umfassend» statt technischer Namen, jeder mit seiner
                Ampel-Farbe und der Feldzahl. So sieht man vor dem Klicken, was
                man bekommt." Beides fehlte — die Karte trug
                Namen und Satz, und man klickte blind.
              */}
              <span className="flex items-center gap-2 text-sm font-medium">
                <span
                  aria-hidden
                  className={
                    presetAmpel(preset).stufe === 'yellow'
                      ? 'rounded-pill bg-warning size-2'
                      : 'rounded-pill bg-success size-2'
                  }
                />
                {presetName(preset, draft.language)}
                <span className="text-muted-foreground font-normal tabular-nums">
                  {presetAmpel(preset).felder}
                </span>
              </span>
              <span className="text-muted-foreground block text-sm">
                {presetBeschreibung(preset, draft.language)}
              </span>
            </button>
          ))}
        </div>

        {pendingPreset ? (
          <div
            className="border-input bg-muted/40 rounded-card flex flex-wrap items-center justify-between gap-2 border p-3 text-sm"
            data-test={dt('preset-confirm')}
          >
            <span>
              <Trans
                i18nKey="self-disclosure:generator.presetConfirm"
                values={{ name: presetName(pendingPreset, draft.language) }}
              />
            </span>
            <span className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant="quiet"
                onClick={onPresetCancel}
                data-test={dt('preset-confirm-cancel')}
              >
                <Trans i18nKey="common:cancel" />
              </Button>
              <Button
                variant="quiet"
                type="button"
                size="sm"
                onClick={() => onPresetClick(pendingPreset)}
                data-test={dt('preset-confirm-apply')}
              >
                <Trans i18nKey="self-disclosure:generator.presetApply" />
              </Button>
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Step 2: Felder (Ampel-Katalog + Rot-Sektion) ──────────────────────────────

function FieldCard({
  field,
  draft,
  onToggle,
  dt,
}: {
  field: SelfDisclosureField;
  draft: GeneratorDraft;
  onToggle: () => void;
  dt: (suffix: string) => string;
}) {
  /*
   * 🔴 Der Katalog las bis zum 23.08.2026 ueberall `.de` — 38 Feldnamen, 18
   * Begruendungen und alle Hinweise standen auf Deutsch, unabhaengig von der
   * Spracheinstellung (§1.25). Jetzt folgt die BEDIENUNG der App-Sprache; das
   * erzeugte FORMULAR bleibt davon unberuehrt (Blatt Q: „Deutsch · English ·
   * Beides").
   */
  const { i18n } = useTranslation();
  const sprache = i18n.language;

  const reason = lockReason(field, draft);
  const locked = reason !== null;
  const checked = draft.enabledFields.includes(field.key);
  const isYellow = field.advice.level === 'yellow';

  return (
    <div
      /*
        🔴 A2: „Deaktivierte oder nicht verfügbare Zustände werden über Farbe
        ausgedrückt, NIE über Transparenz auf dem Container: `opacity` dimmt den
        Text mit und drückt ihn unter 4,5:1. Text bleibt mindestens
        `oklch(0.42 0.02 265)`, nur Flächen und Rahmen werden heller."

        Hier lag `opacity-70` auf dem Kasten — und darin steht der Satz, WARUM
        das Feld gesperrt ist. Genau der wurde mitgedimmt.
      */
      className={
        locked
          ? 'border-input bg-muted/40 text-disabled-foreground rounded-card border p-3'
          : 'border-input rounded-card border p-3'
      }
      data-test={
        locked ? dt(`field-locked-${field.key}`) : dt(`field-${field.key}`)
      }
      data-advice={field.advice.level}
    >
      <label className="flex items-start gap-2">
        <Checkbox
          className="mt-0.5"
          checked={checked}
          disabled={locked}
          onCheckedChange={onToggle}
          data-test={dt(`field-toggle-${field.key}`)}
        />
        <span className="space-y-1">
          <span className="flex items-start gap-2">
            <span
              className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
                isYellow ? 'bg-warning' : 'bg-success'
              }`}
              aria-hidden="true"
            />
            <span className="min-w-0 text-sm font-medium">
              {textFuerSprache(field.label, sprache)}
              {field.optionalForApplicant ? (
                <span className="text-muted-foreground font-normal">
                  {' '}
                  (freiwillig)
                </span>
              ) : null}
            </span>
          </span>
          {isYellow && field.advice.recommendedFrom ? (
            <span className="text-muted-foreground block text-sm">
              {textFuerSprache(
                RECOMMENDED_FROM_LABEL[field.advice.recommendedFrom],
                sprache,
              )}
              {field.advice.reason
                ? ` · ${textFuerSprache(field.advice.reason, sprache)}`
                : null}
            </span>
          ) : null}
          {field.uiHint ? (
            <span className="text-muted-foreground block text-xs">
              {textFuerSprache(field.uiHint, sprache)}
            </span>
          ) : null}
          {reason ? (
            <span className="text-muted-foreground block text-xs italic">
              {reason}
            </span>
          ) : null}
        </span>
      </label>
    </div>
  );
}

// ── Ampel-Legende (Spec §2.4/M4) ──────────────────────────────────────────────

function AdviceLegend({ dt }: { dt: (suffix: string) => string }) {
  return (
    <div
      className="border-input rounded-card space-y-1 border p-3 text-sm"
      data-test={dt('advice-legend')}
    >
      <div className="flex items-start gap-2">
        <span
          className="bg-success mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
          aria-hidden="true"
        />
        <span className="min-w-0">
          <span className="font-medium">
            <Trans i18nKey="self-disclosure:generator.green" />
          </span>{' '}
          kannst du jederzeit fragen.
        </span>
      </div>
      <div className="flex items-start gap-2">
        <span
          className="bg-warning mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
          aria-hidden="true"
        />
        <span className="min-w-0">
          <span className="font-medium">
            <Trans i18nKey="self-disclosure:generator.yellow" />
          </span>{' '}
          ist zulässig – der Hinweis sagt, ab wann empfohlen.
        </span>
      </div>
    </div>
  );
}

/**
 * Income single-choice (Spec §2.2/M1+M2): replaces the two mutually-exclusive
 * income checkboxes with a radio whose value is a pure view over enabledFields.
 * The threshold amount lives INLINE under its option (M2). Under
 * publicPayerDirect the whole control is locked (E3).
 */
function IncomeChoiceControl({
  draft,
  setDraft,
  dt,
  attempted,
  thresholdTouched,
  onMarkTouched,
}: {
  draft: GeneratorDraft;
  setDraft: (updater: (prev: GeneratorDraft) => GeneratorDraft) => void;
  dt: (suffix: string) => string;
  attempted: boolean;
  thresholdTouched: boolean;
  onMarkTouched: (field: TouchedField) => void;
}) {
  const { i18n } = useTranslation();
  const sprache = i18n.language;

  const amountField = getFieldByKey(INCOME_KEYS.amount)!;
  const thresholdField = getFieldByKey(INCOME_KEYS.threshold)!;
  const locked = draft.fieldOptions.publicPayerDirect;
  const choice = currentIncomeChoice(draft);

  const showThresholdError =
    (attempted || thresholdTouched) &&
    choice === 'threshold' &&
    draft.fieldOptions.incomeThresholdEur === undefined;

  return (
    <fieldset
      className="border-input rounded-card space-y-2 border p-3"
      data-test={dt('income-choice')}
      disabled={locked}
    >
      <legend className="text-sm font-medium">
        <Trans i18nKey="self-disclosure:generator.incomeAs" />
      </legend>

      {locked && (
        <p
          className="text-muted-foreground text-xs italic"
          data-test={dt('income-choice-locked')}
        >
          <Trans i18nKey="self-disclosure:generator.incomeLockedHint" />
        </p>
      )}

      <RadioGroup
        value={choice}
        onValueChange={(value) =>
          setDraft((prev) => setIncomeChoice(prev, value as IncomeChoice))
        }
      >
        {/* „Konkreter Betrag" (gelb) – Punkt + muted-Präfix aus amountField.advice */}
        <label className="flex items-start gap-2 text-sm">
          <RadioGroupItem
            value="amount"
            disabled={locked}
            data-test={dt('income-choice-amount')}
            className="mt-0.5"
          />
          <span className="space-y-1">
            <span className="flex items-start gap-2">
              <span
                className="bg-warning mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                aria-hidden="true"
              />
              <span className="min-w-0 font-medium">
                {textFuerSprache(amountField.label, sprache)}
              </span>
            </span>
            {amountField.advice.recommendedFrom ? (
              <span className="text-muted-foreground block">
                {textFuerSprache(
                  RECOMMENDED_FROM_LABEL[amountField.advice.recommendedFrom],
                  sprache,
                )}
                {amountField.advice.reason
                  ? ` · ${textFuerSprache(amountField.advice.reason, sprache)}`
                  : null}
              </span>
            ) : null}
          </span>
        </label>

        {/* „Nur Betragsgrenze bestätigen" (grün) + INLINE Threshold-Input (M2) */}
        <label className="flex items-start gap-2 text-sm">
          <RadioGroupItem
            value="threshold"
            disabled={locked}
            data-test={dt('income-choice-threshold')}
            className="mt-0.5"
          />
          <span className="flex items-start gap-2">
            <span
              className="bg-success mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
              aria-hidden="true"
            />
            <span className="min-w-0 font-medium">
              {thresholdField.label.de}
            </span>
          </span>
        </label>
        {choice === 'threshold' && (
          <div className="ml-6 space-y-1">
            <Label htmlFor={dt('income-threshold')}>
              <Trans i18nKey="self-disclosure:generator.thresholdEuro" />
            </Label>
            <Input
              id={dt('income-threshold')}
              type="number"
              min={1}
              value={draft.fieldOptions.incomeThresholdEur ?? ''}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  fieldOptions: {
                    ...prev.fieldOptions,
                    incomeThresholdEur:
                      event.target.value === ''
                        ? undefined
                        : Number(event.target.value),
                  },
                }))
              }
              onBlur={() => onMarkTouched('threshold')}
              aria-invalid={showThresholdError || undefined}
              data-test={dt('income-threshold')}
            />
            {showThresholdError ? (
              <p
                className="text-destructive text-xs"
                data-test={dt('income-threshold-error')}
              >
                <Trans i18nKey="self-disclosure:generator.incomeThresholdError" />
              </p>
            ) : (
              <p className="text-muted-foreground text-xs">
                üblich das Zwei- bis Dreifache der Kaltmiete.
              </p>
            )}
          </div>
        )}

        {/* „Gar nicht" */}
        <label className="flex items-start gap-2 text-sm">
          <RadioGroupItem
            value="none"
            disabled={locked}
            data-test={dt('income-choice-none')}
            className="mt-0.5"
          />
          <span>
            <Trans i18nKey="self-disclosure:generator.notAtAll" />
          </span>
        </label>
      </RadioGroup>

      {/* OQ6 (§2.2): the threshold confirmation is household-scoped – the
          guarantor does not confirm it. Point users to „Konkreter Betrag". */}
      {draft.includeGuarantor && choice === 'threshold' ? (
        <p
          className="text-muted-foreground text-xs"
          data-test={dt('income-guarantor-hint')}
        >
          <Trans i18nKey="self-disclosure:generator.guarantorThresholdHint" />
        </p>
      ) : null}
    </fieldset>
  );
}

function FieldsStep({
  draft,
  setDraft,
  dt,
  attempted,
  thresholdTouched,
  onMarkTouched,
}: {
  draft: GeneratorDraft;
  setDraft: (updater: (prev: GeneratorDraft) => GeneratorDraft) => void;
  dt: (suffix: string) => string;
  attempted: boolean;
  thresholdTouched: boolean;
  onMarkTouched: (field: TouchedField) => void;
}) {
  return (
    <div className="space-y-4">
      <AdviceLegend dt={dt} />

      {FIELD_SECTION_ORDER.map((section) => {
        const fields = SELF_DISCLOSURE_FIELDS.filter(
          (field) => field.section === section,
        );

        if (fields.length === 0) return null;

        // Denominator = selectable fields only (Spec §2.3/OQ3): locked fields
        // are excluded so the count never shrinks "below" the selected count.
        const selectableCount = fields.filter(
          (field) => lockReason(field, draft) === null,
        ).length;
        const selectedCount = fields.filter((field) =>
          draft.enabledFields.includes(field.key),
        ).length;

        // The two income fields are rendered ONCE as the single-choice control
        // at the first income field's catalog position (Spec §2.2).
        let incomeRendered = false;

        return (
          <Karte
            titel={
              <>
                {FIELD_SECTION_LABELS[section].de}{' '}
                <span className="text-muted-foreground font-normal">
                  {selectedCount}/{selectableCount}
                </span>
              </>
            }
            key={section}
          >
            <div className="space-y-2">
              {fields.map((field) => {
                if (field.incomeVariant) {
                  if (incomeRendered) return null;

                  incomeRendered = true;

                  return (
                    <IncomeChoiceControl
                      key="income-choice"
                      draft={draft}
                      setDraft={setDraft}
                      dt={dt}
                      attempted={attempted}
                      thresholdTouched={thresholdTouched}
                      onMarkTouched={onMarkTouched}
                    />
                  );
                }

                return (
                  <FieldCard
                    key={field.key}
                    field={field}
                    draft={draft}
                    onToggle={() =>
                      setDraft((prev) => toggleField(prev, field))
                    }
                    dt={dt}
                  />
                );
              })}
            </div>
          </Karte>
        );
      })}

      {/*
        ═══════════════════════════════════════════════════════════════════════
        🔴 DIE ROTE SEKTION STEHT OFFEN — Blatt Q · Q3, Befund 453
        ═══════════════════════════════════════════════════════════════════════

        Hier stand „Rot-Block als default-kollabiertes Accordion (Spec §2.5/M5)"
        — eine frühere eigene Festlegung, die Q3 umdreht:

          „Die rote Sektion bleibt am Ende — und bleibt offen. **Nicht
           zugeklappt: sie ist der Grund, warum jemand dem Werkzeug vertraut.**"

        453 zieht das ausdrücklich auf die WEBSITE nach: „Q3 gilt für beide, und
        der Satz sagt selbst warum … Auf der Website ist dieses Vertrauen das
        ganze Produkt — dort steht kein Kunde, sondern jemand, der uns zum
        ersten Mal sieht. **Es gilt: die rote Sektion ist offen, nicht
        zugeklappt — auch mobil, auch wenn sie die Seite verlängert.**"

        `defaultValue` statt Entfernen des Zuklappens: aufklappbar bleibt sie,
        man kann sie also wegräumen, wenn man sie gelesen hat. Nur der
        Anfangszustand dreht sich um. Die App-Fassung
        (`app-generator/fields-section.tsx`) hat genau diese Lösung seit dem
        Q3-Umbau; diese Fassung lief daneben her.

        🔴 Die Überschrift lief bis hierher als deutscher Fixtext im Quelltext —
        auf einer Seite, die es in drei Sprachen gibt, und ausgerechnet an der
        Rechtsbelehrung. Die Zahl kommt weiterhin aus dem Katalog: „18"
        hinzuschreiben hiesse, eine geprüfte Liste an zwei Stellen zu führen.

        ⚠️ Der Haken `forbidden-accordion` BLEIBT stehen. Er trägt die
        Website-Gestaltung über `marketing-extra.css` und hängt an der
        E2E-Suite; ein umbenannter Haken zerlegt beides still.
      */}
      <Accordion type="single" collapsible defaultValue="forbidden">
        <AccordionItem
          value="forbidden"
          className="border-destructive/40 rounded-card border"
        >
          <AccordionTrigger
            className="px-3"
            data-test={dt('forbidden-accordion')}
          >
            <span className="flex items-center gap-2">
              <Ban
                className="text-destructive h-4 w-4 shrink-0"
                aria-hidden="true"
              />
              <Trans
                i18nKey="self-disclosure:generator.forbiddenTitleCount"
                values={{ count: FORBIDDEN_CATEGORIES.length }}
              />
            </span>
          </AccordionTrigger>
          <AccordionContent className="px-3">
            <ForbiddenSection dt={dt} />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

/**
 * Red section "Das darfst du nicht fragen" (Spec §2.6): the 18 forbidden
 * categories as a NON-interactive list – no input element, `aria-disabled`,
 * a one-liner why per category. The renderer can structurally never produce
 * them; this section exists for legal education, not selection.
 */
function ForbiddenSection({ dt }: { dt: (suffix: string) => string }) {
  const { i18n } = useTranslation();
  const sprache = i18n.language;

  return (
    <div
      className="border-destructive/40 rounded-card space-y-2 border p-3"
      data-test={dt('forbidden-section')}
      aria-disabled="true"
    >
      <p className="text-sm font-medium">
        <Trans i18nKey="self-disclosure:generator.forbiddenTitle" />
      </p>
      <p className="text-muted-foreground text-sm">
        <Trans i18nKey="self-disclosure:generator.forbiddenCategories" />
      </p>
      <ul className="space-y-2">
        {FORBIDDEN_CATEGORIES.map((category) => (
          <li
            key={category.key}
            className="flex items-start gap-2"
            data-test={dt(`forbidden-${category.key}`)}
            aria-disabled="true"
          >
            {/*
              🔴 Blatt Q3: „Ein 8-px-Punkt je Zeile, dieselbe Farbe wie in der
              Legende darueber. Symbole (Haekchen, Warndreieck) waeren lauter
              als die Feldnamen — man scannt hier eine Liste, nicht einen
              Warnhinweis." Die gruenen und gelben Zeilen
              tragen schon einen Punkt; 18 Verbots-Symbole untereinander waren
              lauter als alles daneben.
            */}
            <span
              className="bg-destructive mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
              aria-hidden="true"
            />
            <span>
              <span className="block text-sm font-medium">
                {textFuerSprache(category.label, sprache)}
              </span>
              <span className="text-muted-foreground block text-xs">
                {textFuerSprache(category.reason, sprache)}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Step 3: Absender ──────────────────────────────────────────────────────────

function SenderField({
  label,
  value,
  onChange,
  dataTest,
  type = 'text',
  placeholder,
  hint,
  required = false,
  error,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  dataTest: string;
  type?: string;
  placeholder?: string;
  hint?: string;
  /** Marks the field with a „*" and `aria-required` (Spec §2.7/M10). */
  required?: boolean;
  /** Contextual validation message, shown below the input (Spec §I.2). */
  error?: string;
  onBlur?: () => void;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={dataTest}>
        {label}
        {required ? (
          <span aria-hidden="true" className="text-destructive">
            {' '}
            *
          </span>
        ) : null}
      </Label>
      <Input
        id={dataTest}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        data-test={dataTest}
      />
      {error ? (
        <p className="text-destructive text-xs" data-test={`${dataTest}-error`}>
          {error}
        </p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs">{hint}</p>
      ) : null}
    </div>
  );
}

function SenderStep({
  draft,
  setDraft,
  dt,
  attempted,
  touched,
  onMarkTouched,
  errors,
}: {
  draft: GeneratorDraft;
  setDraft: (updater: (prev: GeneratorDraft) => GeneratorDraft) => void;
  dt: (suffix: string) => string;
  attempted: boolean;
  touched: UiState['touched'];
  onMarkTouched: (field: TouchedField) => void;
  errors: string[];
}) {
  const setSender = (patch: Partial<GeneratorDraft['sender']>) =>
    setDraft((prev) => ({ ...prev, sender: { ...prev.sender, ...patch } }));

  const showDisplayNameError =
    (attempted || touched.displayName) &&
    draft.sender.displayName.trim() === '';

  const showStreetError =
    (attempted || touched.street) && errors.includes(STREET_MESSAGE);

  const showPostalCodeError =
    (attempted || touched.postalCode) && errors.includes(POSTAL_CODE_MESSAGE);

  const showCityError =
    (attempted || touched.city) && errors.includes(CITY_MESSAGE);

  const showEmailError =
    (attempted || touched.email) && errors.includes(EMAIL_MESSAGE);

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        Der Absender-Kopf erscheint als verantwortliche Stelle (Art. 13 DSGVO)
        im Formular. Diese Angaben betreffen ausschließlich dich als Vermieter.
      </p>

      <OptionToggle
        label="Ich bin ein Unternehmen / eine Hausverwaltung"
        checked={draft.sender.isCompany}
        onChange={(checked) => setSender({ isCompany: checked })}
        dataTest={dt('sender-is-company')}
      />

      <SenderField
        label={draft.sender.isCompany ? 'Firmenname' : 'Name des Vermieters'}
        value={draft.sender.displayName}
        onChange={(value) => setSender({ displayName: value })}
        dataTest={dt('sender-display-name')}
        required
        error={showDisplayNameError ? DISPLAY_NAME_MESSAGE : undefined}
        onBlur={() => onMarkTouched('displayName')}
      />

      {draft.sender.isCompany && (
        <SenderField
          label="Ansprechpartner (optional)"
          value={draft.sender.contactPerson}
          onChange={(value) => setSender({ contactPerson: value })}
          dataTest={dt('sender-contact-person')}
        />
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <SenderField
          label="Straße und Hausnummer"
          value={draft.sender.street}
          onChange={(value) => setSender({ street: value })}
          dataTest={dt('sender-street')}
          required
          error={showStreetError ? STREET_MESSAGE : undefined}
          onBlur={() => onMarkTouched('street')}
        />
        <SenderField
          label="PLZ"
          value={draft.sender.postalCode}
          onChange={(value) => setSender({ postalCode: value })}
          dataTest={dt('sender-postal-code')}
          required
          error={showPostalCodeError ? POSTAL_CODE_MESSAGE : undefined}
          onBlur={() => onMarkTouched('postalCode')}
        />
        <SenderField
          label="Ort"
          value={draft.sender.city}
          onChange={(value) => setSender({ city: value })}
          dataTest={dt('sender-city')}
          required
          error={showCityError ? CITY_MESSAGE : undefined}
          onBlur={() => onMarkTouched('city')}
        />
        <SenderField
          label="E-Mail (empfohlen)"
          type="email"
          value={draft.sender.email}
          onChange={(value) => setSender({ email: value })}
          dataTest={dt('sender-email')}
          error={showEmailError ? EMAIL_MESSAGE : undefined}
          onBlur={() => onMarkTouched('email')}
        />
      </div>

      <SenderField
        label="Objektbezeichnung (optional)"
        placeholder="z. B. Wohnung Musterstraße 1, 2. OG"
        value={draft.sender.propertyLabel}
        onChange={(value) => setSender({ propertyLabel: value })}
        dataTest={dt('sender-property-label')}
      />

      <SenderField
        label="Datenschutzbeauftragter (optional)"
        value={draft.sender.dpoContact}
        onChange={(value) => setSender({ dpoContact: value })}
        dataTest={dt('sender-dpo-contact')}
        hint="Nur ausfüllen, wenn du tatsächlich einen Datenschutzbeauftragten hast – als Privatvermieter hast du in der Regel keinen."
      />
    </div>
  );
}
